import { useCallback, useContext, useMemo } from 'react';
import { useCookies } from 'react-cookie';
import { useSearchParams } from 'react-router-dom';
import { UserContext } from '../../context';
import { NewsFeedScope, StoryCardStatus } from '../../services';
import {
  getNewsFeed,
  getPinnedFeed,
  getSubTabFeed,
  loadMoreNewsFeed,
  loadMorePinnedFeed,
  loadMoreSubTabFeed,
} from '../../slices';
import { NEWS_FEED_ADMIN_COOKIE_KEY } from '../constants';
import { isAdminLayout } from '../utils';
import { useAppDispatch } from './useAppDispatch';
import { useAppSelector } from './useAppSelector';
import { useIsPersonalizationEnabled } from './useIsPersonalizationEnabled';
import { useLastDate } from './useLastDate';

export const useNewsFeed = () => {
  const dispatch = useAppDispatch();

  const { getDateRange } = useLastDate();

  const [cookies] = useCookies([NEWS_FEED_ADMIN_COOKIE_KEY]);

  const [searchParams] = useSearchParams();

  const { page } = useAppSelector(({ newsFeed }) => newsFeed);

  const { channelId } = useContext(UserContext).userInfo.userData;

  const isPersonalizationEnabled = useIsPersonalizationEnabled();

  const { pinnedStorySection } = cookies[NEWS_FEED_ADMIN_COOKIE_KEY]?.requestFilter ?? {};

  const subTabStoryId = useMemo(
    () => Number(searchParams.get('storyId')) || undefined,
    [searchParams]
  );

  const readerFilter = useMemo(() => {
    return { status: [StoryCardStatus.PUBLISHED] };
  }, []);

  const adminFilter = useMemo(() => {
    const { requestFilter = { scope: NewsFeedScope.WEB_FEED } } = {
      ...cookies[NEWS_FEED_ADMIN_COOKIE_KEY],
    };

    const { dateRange, pinnedStorySection, ...restFilter } = requestFilter;

    return { ...restFilter, ...getDateRange(dateRange) };
  }, [cookies, getDateRange]);

  const filter = isAdminLayout() ? adminFilter : readerFilter;

  const storyFeedFilter = useMemo(() => {
    const { feedFollowBehavior, ...restFilter } = filter;

    return restFilter;
  }, [filter]);

  const getNewsFeedItems = useCallback(
    () => dispatch(getNewsFeed({ channelId, filter, isPersonalizationEnabled })),
    [channelId, dispatch, filter, isPersonalizationEnabled]
  );

  const loadMoreNewsFeedItems = useCallback(
    () =>
      dispatch(loadMoreNewsFeed({ channelId, page: page + 1, filter, isPersonalizationEnabled })),
    [channelId, dispatch, filter, page, isPersonalizationEnabled]
  );

  const getPinnedFeedItems = useCallback(
    (pinnedStoryId: number) =>
      dispatch(getPinnedFeed({ storyId: pinnedStoryId, filter: storyFeedFilter })),
    [dispatch, storyFeedFilter]
  );

  const loadMorePinnedFeedItems = useCallback(
    (pinnedStoryId: number) =>
      dispatch(
        loadMorePinnedFeed({ storyId: pinnedStoryId, page: page + 1, filter: storyFeedFilter })
      ),
    [dispatch, page, storyFeedFilter]
  );

  const getSubTabFeedItems = useCallback(() => {
    if (!subTabStoryId) {
      return;
    }
    dispatch(getSubTabFeed({ storyId: subTabStoryId, filter: storyFeedFilter }));
  }, [dispatch, storyFeedFilter, subTabStoryId]);

  const loadMoreSubTabFeedItems = useCallback(() => {
    if (!subTabStoryId) {
      return;
    }
    dispatch(
      loadMoreSubTabFeed({ storyId: subTabStoryId, page: page + 1, filter: storyFeedFilter })
    );
  }, [dispatch, page, storyFeedFilter, subTabStoryId]);

  return {
    getNewsFeedItems,
    loadMoreNewsFeedItems,
    getPinnedFeedItems,
    loadMorePinnedFeedItems,
    pinnedStorySection,
    getSubTabFeedItems,
    loadMoreSubTabFeedItems,
    subTabStoryId,
  };
};
